/**
 * The app's home when no session is open — FR-SESS-6, UI-13.
 *
 * This replaces a screen that was a dead end in two ways at once. It listed
 * only today's uncommitted sessions, so a member who created one for tomorrow
 * could never open it again; and it rendered no tab bar, so from it there was
 * no route to the standings, the club board or the account screen. The only
 * thing you could do was create another session — which is how two identical
 * sessions came to exist for 2 August.
 *
 * An agenda, not a month grid. The club has played 59 sessions in 26 months —
 * about 2.2 a month — so a 30-cell grid would carry two marks and answer "is
 * there badminton tomorrow, where, what time" only after a tap into a detail
 * sheet. The agenda answers it in none. A seven-column grid is also under 40px
 * per cell at the 320px width UI-06 requires, which is below a tap target
 * before any padding.
 *
 * Upcoming first, because the question that brings somebody here is almost
 * always about the next session rather than the last one.
 */

import { useCallback, useEffect, useState } from "react";
import { api, type Me, type SessionListItem } from "../api.js";
import { ClubBoard } from "./ClubBoard.js";
import { StartTournament } from "./StartTournament.js";
import { YouScreen } from "./YouScreen.js";
import { formatSessionDate } from "./StartSession.js";

type Tab = "sessions" | "club" | "you";

export function SessionsScreen({
  me,
  onOpen,
  onSignedOut,
  onShowGuide,
}: {
  me: Me;
  onOpen: (sessionId: string) => void;
  onSignedOut: () => void;
  onShowGuide?: () => void;
}): React.JSX.Element {
  const [list, setList] = useState<Awaited<ReturnType<typeof api.sessions>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** FR-TOUR-1 — admin only, and a different screen: a draw, not a session. */
  const [creatingTournament, setCreatingTournament] = useState(false);
  /** The session being edited, if any. The form serves both jobs. */
  const [editing, setEditing] = useState<SessionListItem | null>(null);
  const [tab, setTab] = useState<Tab>("sessions");

  const load = useCallback(() => {
    api
      .sessions()
      .then(setList)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not load the sessions."),
      );
  }, []);

  useEffect(load, [load]);

  /**
   * FR-ADMIN-4 — remove the row a mis-tap produced.
   *
   * Offered only to an admin, and only on a session with nothing in it: the
   * server refuses anything holding a game or an attendee, so this is the
   * screen agreeing with the rule rather than enforcing it.
   */
  const discard =
    me.role === "admin"
      ? (session: SessionListItem): void => {
          const marked =
            session.attendeeCount > 0
              ? `\n\n${String(session.attendeeCount)} ${
                  session.attendeeCount === 1 ? "person is" : "people are"
                } marked present, and that goes too. No games were logged.`
              : "";
          const reason = window.prompt(
            `Discard the session on ${formatSessionDate(session.date)} at ${
              session.venue ?? "no venue"
            }?${marked}\n\nSay why:`,
            "Created twice by mistake",
          );
          if (!reason) return;
          api
            .discardSession(session.id, reason)
            .then(load)
            .catch((e: unknown) =>
              setError(e instanceof Error ? e.message : "Could not discard that session."),
            );
        }
      : undefined;

  if (creatingTournament) {
    return (
      <StartTournament
        me={me}
        onCancel={() => setCreatingTournament(false)}
        onCreated={onOpen}
      />
    );
  }

  if (creating || editing) {
    return (
      <SessionForm
        me={me}
        editing={editing}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={(id) => {
          if (editing) {
            setEditing(null);
            load();
          } else {
            onOpen(id);
          }
        }}
      />
    );
  }

  return (
    <>
    <main className="session">
      <header className="session-head">
        <div className="brand">
          <h1 className="display">
            Core <span>Baddy</span>
          </h1>
          <div className="meta">{me.displayName}</div>
        </div>
        {tab === "sessions" ? (
          <button className="mini" onClick={() => setCreating(true)}>
            + New
          </button>
        ) : null}
      </header>

      {tab === "club" ? <ClubBoard /> : null}
      {tab === "you" ? <YouScreen
          me={me}
          onSignedOut={onSignedOut}
          onOpenSession={onOpen}
          onShowGuide={onShowGuide}
        /> : null}

      {tab === "sessions" && error ? <p className="error">{error}</p> : null}
      {tab === "sessions" && !list && !error ? <p className="muted small">Loading…</p> : null}

      {tab === "sessions" && list ? (
        <>
          {/* The session being played right now, if there is one. */}
          <Group
            title="Today"
            sessions={list.today_}
            onOpen={onOpen}
            onDiscard={discard}
            onEdit={setEditing}
          />
          <Group
            title="Coming up"
            sessions={list.upcoming}
            onOpen={onOpen}
            onDiscard={discard}
            onEdit={setEditing}
          />
          <Group title="Recent" sessions={list.recent} onOpen={onOpen} past />

          {list.today_.length === 0 && list.upcoming.length === 0 && list.recent.length === 0 ? (
            <section className="block">
              <h3 className="card-head">No sessions yet</h3>
              <p>
                Create one for today or for a date ahead — the club&apos;s usual
                venue, time and court count are already filled in.
              </p>
            </section>
          ) : null}

          {list.hasMore ? (
            <p className="muted small">
              Older sessions are not listed here. Their games still count
              towards every all-time figure.
            </p>
          ) : null}

          <button className="primary" onClick={() => setCreating(true)}>
            Start a session
          </button>

          {/* A tournament is a session with its fixtures already decided, so
              it starts from the same place — but it is a rarer thing and an
              admin one, so it sits below rather than competing with the
              button the club presses every Saturday. */}
          {me.role === "admin" ? (
            <button className="quiet" onClick={() => setCreatingTournament(true)}>
              Set up a tournament
            </button>
          ) : null}
        </>
      ) : null}

    </main>
      {/*
        A reduced bar, not the session one.
        
        The five-tab bar is about a session in progress: Play, Games and End all
        need one open. With no session there are exactly three things to do —
        pick or create one, look at the club's all-time figures, or manage your
        account — and offering two disabled tabs would be worse than offering
        three that work.

        Without this the screen was a dead end: no route to the statistics or
        the account screen at all unless a session happened to be open, which is
        the other half of what the club reported.
      */}
      <nav className="tabs">
        {(
          [
            { id: "sessions", short: "Play", long: "Sessions" },
            { id: "club", short: "Table", long: "All time" },
            { id: "you", short: "You", long: "Account" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            className={t.id === tab ? "on" : undefined}
            aria-current={t.id === tab ? "page" : undefined}
            onClick={() => setTab(t.id)}
          >
            <b>{t.short}</b>
            {t.long}
          </button>
        ))}
      </nav>
    </>
  );
}

function Group({
  title,
  sessions,
  onOpen,
  onDiscard,
  onEdit,
  past,
}: {
  title: string;
  sessions: SessionListItem[];
  onOpen: (id: string) => void;
  onDiscard?: (session: SessionListItem) => void;
  onEdit?: (session: SessionListItem) => void;
  past?: boolean;
}): React.JSX.Element | null {
  if (sessions.length === 0) return null;

  return (
    <section className="block">
      <h3 className="card-head">{title}</h3>
      {sessions.map((s) => (
        <div key={s.id} className="sessionrow-wrap">
        <button className="sessionrow" onClick={() => onOpen(s.id)}>
          <span className="sessionrow-when">
            {formatSessionDate(s.date)}
            <span className="muted"> · {s.venue ?? "Session"}</span>
          </span>
          {/*
            A future session is a plan, so it says what is arranged. A past one
            is a record, so it says what happened. Same row, different question.
          */}
          <span className="muted small">
            {past || s.gameCount > 0
              ? `${String(s.gameCount)} ${s.gameCount === 1 ? "game" : "games"} · ${
                  s.status === "committed" ? "finished" : "not finished"
                }`
              : [
                  s.startTime ? s.startTime.slice(0, 5) : null,
                  `${String(s.courtCount)} ${s.courtCount === 1 ? "court" : "courts"}`,
                  `to ${String(s.targetScore)}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
          </span>
        </button>
        {/* The date, venue, courts and target of a session that has not been
            finished. FR-SESS-3 — editable without affecting anything else. */}
        {onEdit && s.status !== "committed" ? (
          <button className="mini" onClick={() => onEdit(s)}>
            Edit
          </button>
        ) : null}
        {/* No games and not finished: the only kind that should ever go.
            Roster markings come with it, and the confirmation says so. */}
        {onDiscard && s.gameCount === 0 && s.status !== "committed" ? (
          <button className="mini" onClick={() => onDiscard(s)}>
            Discard
          </button>
        ) : null}
        </div>
      ))}
    </section>
  );
}

/**
 * FR-SESS-4, FR-SESS-3, SESS-05 — one screen, two jobs.
 *
 * Creating is everything pre-filled from the club's last session, with nothing
 * required. Editing is the same form with this session's values in it. They
 * are the same fields answering the same questions, and two forms would drift.
 */
function SessionForm({
  me,
  editing,
  onCancel,
  onSaved,
}: {
  me: Me;
  editing: SessionListItem | null;
  onCancel: () => void;
  onSaved: (sessionId: string) => void;
}): React.JSX.Element {
  const [date, setDate] = useState("");
  const [venue, setVenue] = useState("");
  const [courtCount, setCourtCount] = useState(1);
  const [target, setTarget] = useState(15);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) {
      setDate(editing.date);
      setVenue(editing.venue ?? "");
      setCourtCount(editing.courtCount);
      setTarget(editing.targetScore);
      return;
    }
    api
      .today()
      .then(({ defaults }) => {
        // The club's today, in the club's timezone — not the phone's, which is
        // a different date for anyone travelling.
        setDate(defaults.date);
        setVenue(defaults.venue ?? "");
        setCourtCount(defaults.courtCount);
        setTarget(defaults.targetScore);
      })
      .catch(() => undefined);
  }, [editing]);

  return (
    <main className="session">
      <header className="session-head">
        <div className="brand">
          <h1 className="display">{editing ? "Edit session" : "New session"}</h1>
          <div className="meta">{me.displayName}</div>
        </div>
        <button className="link" onClick={onCancel}>
          Back
        </button>
      </header>

      <div className="block stack">
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>

        <label>
          Venue
          <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="SBH Expo" />
        </label>

        <label>
          Courts
          <div className="row">
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                className={n === courtCount ? "chip chip-on" : "chip"}
                onClick={() => setCourtCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </label>

        <label>
          Games to
          <div className="row">
            {[15, 21, 11].map((n) => (
              <button
                key={n}
                type="button"
                className={n === target ? "chip chip-on" : "chip"}
                onClick={() => setTarget(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </label>

        <p className="muted small">
          This is the default for the session. Any single game can be played to
          a different target — that is chosen on the Play screen, and changing
          it here never alters a game already logged.
        </p>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <button
        className="primary"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          const fields = {
              date: date || undefined,
              venue: venue.trim() || null,
              courtCount,
              targetScore: target,
            };
          (editing ? api.updateSession(editing.id, fields) : api.createSession(fields))
            .then((session) => onSaved(session.id))
            .catch((e: unknown) =>
              setError(
                e instanceof Error
                  ? e.message
                  : editing
                    ? "Could not save those changes."
                    : "Could not start the session.",
              ),
            )
            .finally(() => setBusy(false));
        }}
      >
        {editing ? "Save" : "Create"}
      </button>
    </main>
  );
}
